<template>
  <div class="preview-section mb-4">
    <div v-if="selectedElement" :class="['card', { 'readonly-mode': props.readOnly }]">
      <cardHeader
        :numOrder="getQuestionNumber() === 'N/A' ? numOrder : getQuestionNumber()"
        :elementType="selectedElement.el_Type"
        :mode="'Render'"
        :isAgent="isAgent" />

      <div class="card-body">
        <!-- Main Prompt -->
        <div class="mb-2">
          <h3>{{ selectedElement.el_Text }}</h3>
        </div>

        <!-- Rich Text Content -->
        <div class="quill-editor" ref="quillEditor" v-show="hasQuillContent"></div>

    <!-- Drawing Canvas -->
    <div class="drawing-area">
      <div class="canvas-wrapper" :style="canvasStyle">
        <canvas
          ref="canvasRef"
          :width="selectedElement.drawingConfig?.canvasWidth || 800"
          :height="selectedElement.drawingConfig?.canvasHeight || 600"
          @mousedown="startDrawing"
          @mousemove="draw"
          @mouseup="stopDrawing"
          @mouseleave="handleMouseLeave"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="stopDrawing"
        ></canvas>
        <!-- Eraser cursor overlay -->
        <div
          v-if="currentTool === 'eraser' && eraserPos"
          class="eraser-cursor"
          :style="{
            left: eraserPos.x + 'px',
            top: eraserPos.y + 'px',
            width: strokeWidth + 'px',
            height: strokeWidth + 'px'
          }"
        ></div>
        <!-- Text labels overlay -->
        <div
          v-for="(label, index) in textLabels"
          :key="index"
          class="text-label"
          :style="{
            left: label.x + 'px',
            top: label.y + 'px',
            fontSize: label.fontSize + 'px',
            fontFamily: label.fontFamily,
            color: label.color,
            cursor: draggingLabel === index ? 'move' : 'grab'
          }"
          @mousedown="startDragLabel(index, $event)"
          @touchstart="startDragLabel(index, $event)"
        >
          {{ label.text }}
          <button
            class="delete-label-btn"
            @click.stop="deleteLabel(index)"
            title="Delete label"
          >×</button>
        </div>
        <!-- Text input modal -->
        <div v-if="showTextModal" class="text-input-modal">
          <div class="modal-content">
            <h5>Enter text:</h5>
            <input
              ref="textInputRef"
              v-model="textInputValue"
              type="text"
              class="form-control"
              @keyup.enter="confirmTextInput"
              @keyup.esc="cancelTextInput"
              placeholder="Type your text here..."
            />
            <div class="modal-buttons">
              <button class="btn btn-sm btn-secondary" @click="cancelTextInput">Cancel</button>
              <button class="btn btn-sm btn-primary" @click="confirmTextInput">OK</button>
            </div>
          </div>
        </div>
        <!-- Text Block Overlay -->
        <div v-if="showTextBlock" class="text-block-overlay" :class="'position-' + textBlockPosition">
          <div class="text-block-header">
            <span>Text Block</span>
            <div class="position-buttons">
              <button @click="setTextBlockPosition('top')" :class="{ active: textBlockPosition === 'top' }" title="Top">⬆</button>
              <button @click="setTextBlockPosition('left')" :class="{ active: textBlockPosition === 'left' }" title="Left">⬅</button>
              <button @click="setTextBlockPosition('bottom')" :class="{ active: textBlockPosition === 'bottom' }" title="Bottom">⬇</button>
              <button @click="setTextBlockPosition('right')" :class="{ active: textBlockPosition === 'right' }" title="Right">➡</button>
            </div>
          </div>
          <textarea
            v-model="textBlockContent"
            class="text-block-textarea"
            :style="{
              fontSize: (selectedElement.drawingConfig?.textBlockConfig?.fontSize || 14) + 'px',
              fontFamily: selectedElement.drawingConfig?.textBlockConfig?.fontFamily || 'Arial',
              padding: (selectedElement.drawingConfig?.textBlockConfig?.padding || 10) + 'px',
              backgroundColor: selectedElement.drawingConfig?.textBlockConfig?.backgroundColor || '#ffffff'
            }"
            @input="saveResponse"
            placeholder="Type your answer here..."></textarea>
        </div>
      </div>

      <!-- Drawing Tools -->
      <div class="tools-panel mt-3">
        <div class="btn-group" role="group">
          <button
            v-if="selectedElement.drawingConfig?.tools?.pen !== false"
            type="button"
            class="btn btn-sm btn-outline-primary"
            :class="{ active: currentTool === 'pen' }"
            @click="selectTool('pen')">
            ✏️ Pen
          </button>
          <button
            v-if="selectedElement.drawingConfig?.tools?.eraser"
            type="button"
            class="btn btn-sm btn-outline-secondary"
            :class="{ active: currentTool === 'eraser' }"
            @click="selectTool('eraser')">
            🧹 Eraser
          </button>
          <button
            v-if="selectedElement.drawingConfig?.tools?.shapes"
            type="button"
            class="btn btn-sm btn-outline-info"
            :class="{ active: currentTool === 'rectangle' }"
            @click="selectTool('rectangle')">
            ▢ Rect
          </button>
          <button
            v-if="selectedElement.drawingConfig?.tools?.shapes"
            type="button"
            class="btn btn-sm btn-outline-info"
            :class="{ active: currentTool === 'circle' }"
            @click="selectTool('circle')">
            ○ Circle
          </button>
          <button
            v-if="selectedElement.drawingConfig?.tools?.shapes"
            type="button"
            class="btn btn-sm btn-outline-info"
            :class="{ active: currentTool === 'line' }"
            @click="selectTool('line')">
            ╱ Line
          </button>
          <button
            v-if="selectedElement.drawingConfig?.tools?.text"
            type="button"
            class="btn btn-sm btn-outline-success"
            :class="{ active: currentTool === 'text' }"
            @click="selectTool('text')">
            A Label
          </button>
          <button
            v-if="selectedElement.drawingConfig?.tools?.textBlock"
            type="button"
            class="btn btn-sm btn-outline-primary"
            :class="{ active: showTextBlock }"
            @click="toggleTextBlock">
            📝 Block
          </button>
        </div>

        <!-- Color Picker -->
        <div v-if="selectedElement.drawingConfig?.tools?.colors" class="color-picker ms-2">
          <label class="small">Color:</label>
          <input
            type="color"
            v-model="currentColor"
            class="form-control form-control-sm"
            style="width: 50px; height: 32px;"
          />
        </div>

        <!-- Stroke Width -->
        <div class="stroke-width ms-2">
          <label class="small">Width:</label>
          <input
            type="range"
            v-model.number="strokeWidth"
            :min="selectedElement.drawingConfig?.strokeWidth?.min || 1"
            :max="selectedElement.drawingConfig?.strokeWidth?.max || 20"
            class="form-range"
            style="width: 80px;"
          />
          <span class="small ms-1">{{ strokeWidth }}px</span>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons ms-auto">
          <button
            v-if="selectedElement.drawingConfig?.tools?.undo !== false"
            type="button"
            class="btn btn-sm btn-outline-warning"
            @click="undo"
            :disabled="historyStep <= 0">
            ↶
          </button>
          <button
            v-if="selectedElement.drawingConfig?.tools?.undo !== false"
            type="button"
            class="btn btn-sm btn-outline-warning"
            @click="redo"
            :disabled="historyStep >= history.length - 1">
            ↷
          </button>
          <button
            v-if="selectedElement.drawingConfig?.tools?.clear !== false"
            type="button"
            class="btn btn-sm btn-outline-danger"
            @click="clearCanvas">
            🗑️
          </button>
        </div>
      </div>
    </div>

        <!-- Info about manual correction -->
        <div hidden v-if="selectedElement.scoringMode === 'manual'" class="alert alert-info mt-3 small">
          ℹ️ This drawing will be manually corrected by your instructor.
        </div>

        <!-- Report Container -->
        <div class="ReportContainer" v-if="showReport">
          <div class="row">
            <div class="col">
              <h5>Results:</h5>
            </div>
            <div class="col" style="text-align: right;"><strong>Date:</strong> {{ currentDate }}</div>
            <hr />
            <p><strong>Drawing submitted:</strong> {{ results.hasDrawing ? 'Yes' : 'No' }}</p>
            <p><strong>Score:</strong> {{ results.score }}</p>
            <p><strong>Max Score:</strong> {{ selectedElement.maxScore || 1 }}</p>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <cardFooterLearningMode
        v-if="publicationStore.publicationData?.mode !== 'exam'"
        :showReport="showReport"
        :submitAnswers="showReportOnSubmit"
        :closeReport="closeReport"
        :resetSelections="resetAnswers" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, onBeforeUnmount, nextTick } from 'vue'
import Quill from 'quill'
import cardHeader from './cardHeader.vue'
import cardFooterLearningMode from './cardFooterLearningMode.vue'
import { useResponsesStore } from '@/stores/useResponsesStore'
import { usePublicationStore } from '@/stores/usePublicationStore'

const props = defineProps({
  question: { type: Object, required: true },
  testData: { type: Object, required: true },
  numOrder: [Number, null],
  questionIndexMap: { type: Object, required: false },
  restoredAnswer: { type: Object, default: () => ({}) },
  readOnly: { type: Boolean, default: false },
  isAgent: { type: Boolean, default: false }
})

const emit = defineEmits(['interaction-started'])

// Stores
const responsesStore = useResponsesStore()
const publicationStore = usePublicationStore()

// Refs
const canvasRef = ref(null)
const quillEditor = ref(null)
let quillInstance = null
const ctx = ref(null)
const isDrawing = ref(false)
const currentTool = ref('pen')
const currentColor = ref('#000000')
const strokeWidth = ref(3)
const startPos = ref({ x: 0, y: 0 })
const history = ref([])
const historyStep = ref(-1)
const eraserPos = ref(null)
const tempCanvas = ref(null)
const fontSize = ref(20)
const fontFamily = ref('Arial')
const textLabels = ref([])
const draggingLabel = ref(null)
const dragOffset = ref({ x: 0, y: 0 })
const showTextModal = ref(false)
const textInputValue = ref('')
const textInputRef = ref(null)
const pendingTextPos = ref({ x: 0, y: 0 })
const showTextBlock = ref(false)
const textBlockContent = ref('')
const textBlockPosition = ref('bottom')

// State
const selectedElement = computed(() => props.question)
const showReport = ref(false)
const currentDate = ref(new Date().toLocaleString())

// User identification
const token = localStorage.getItem('token')
const decodedToken = decodeToken(token)
const userId = decodedToken?._id || decodedToken?.sub

// Computed
const hasQuillContent = computed(() => {
  return selectedElement.value?.el_RichText && selectedElement.value.el_RichText.trim() !== ''
})

const getQuestionNumber = () => {
  if (!selectedElement.value || !props.questionIndexMap) return 'N/A'
  return props.questionIndexMap[selectedElement.value.el_ID] || 'N/A'
}

const canvasStyle = computed(() => ({
  backgroundColor: selectedElement.value?.drawingConfig?.backgroundColor || '#ffffff',
  backgroundImage: selectedElement.value?.drawingConfig?.backgroundImage
    ? `url(${selectedElement.value.drawingConfig.backgroundImage})`
    : 'none',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  border: '2px solid #ddd',
  borderRadius: '4px',
  display: 'inline-block'
}))

// Results calculation
const results = computed(() => {
  return {
    hasDrawing: history.value.length > 0,
    score: 0,
    maxScore: selectedElement.value?.maxScore || 1
  }
})

const initCanvas = () => {
  if (canvasRef.value) {
    ctx.value = canvasRef.value.getContext('2d')
    ctx.value.lineCap = 'round'
    ctx.value.lineJoin = 'round'

    // Load saved response if exists
    // Support both new (flat) and old (nested) formats for backward compatibility
    const storedResponse = responsesStore.getResponseForQuestion(userId, selectedElement.value.el_ID)
    const savedResponse = props.restoredAnswer?.drawingData ||
                         storedResponse?.drawingData || // New flat format
                         storedResponse?.response?.drawingData // Old nested format (backward compatibility)

    if (savedResponse) {
      const img = new Image()
      img.onload = () => {
        ctx.value.drawImage(img, 0, 0)
        saveToHistory()
      }
      img.src = savedResponse

      // Restore textBlock if saved (support both formats)
      const textBlockData = props.restoredAnswer?.textBlockContent ||
                           storedResponse?.textBlockContent || // New flat format
                           storedResponse?.response?.textBlockContent // Old nested format
      if (textBlockData) {
        textBlockContent.value = textBlockData
        showTextBlock.value = true
      }

      // Restore textBlock position (support both formats)
      const textBlockPos = props.restoredAnswer?.textBlockPosition ||
                          storedResponse?.textBlockPosition || // New flat format
                          storedResponse?.response?.textBlockPosition // Old nested format
      if (textBlockPos) {
        textBlockPosition.value = textBlockPos
      }

      // Restore text labels if saved (support both formats)
      const savedLabels = props.restoredAnswer?.textLabels ||
                         storedResponse?.textLabels || // New flat format
                         storedResponse?.response?.textLabels // Old nested format
      if (savedLabels) {
        textLabels.value = savedLabels
      }
    } else {
      // Set background
      const bgColor = selectedElement.value?.drawingConfig?.backgroundColor || '#ffffff'
      ctx.value.fillStyle = bgColor
      ctx.value.fillRect(0, 0, canvasRef.value.width, canvasRef.value.height)

      // Load background image if exists
      if (selectedElement.value?.drawingConfig?.backgroundImage) {
        const img = new Image()
        img.onload = () => {
          ctx.value.drawImage(img, 0, 0, canvasRef.value.width, canvasRef.value.height)
          saveToHistory()
        }
        img.src = selectedElement.value.drawingConfig.backgroundImage
      } else {
        saveToHistory()
      }
    }

    strokeWidth.value = selectedElement.value?.drawingConfig?.strokeWidth?.default || 3

    // Initialize text settings if available
    if (selectedElement.value?.drawingConfig?.textConfig) {
      fontSize.value = selectedElement.value.drawingConfig.textConfig.fontSize || 20
      fontFamily.value = selectedElement.value.drawingConfig.textConfig.fontFamily || 'Arial'
    }
  }
}

const selectTool = (tool) => {
  currentTool.value = tool;
};

const getCanvasCoordinates = (e) => {
  const rect = canvasRef.value.getBoundingClientRect();
  const scaleX = canvasRef.value.width / rect.width;
  const scaleY = canvasRef.value.height / rect.height;

  return {
    x: (e.clientX - rect.left) * scaleX,
    y: (e.clientY - rect.top) * scaleY
  };
};

const startDrawing = (e) => {
  const pos = getCanvasCoordinates(e);

  if (currentTool.value === 'text') {
    // Text tool: show modal for input
    pendingTextPos.value = pos;
    showTextModal.value = true;
    textInputValue.value = '';
    // Focus input after modal appears
    setTimeout(() => {
      if (textInputRef.value) {
        textInputRef.value.focus();
      }
    }, 50);
    return;
  }

  isDrawing.value = true;
  startPos.value = pos;

  if (currentTool.value === 'pen' || currentTool.value === 'eraser') {
    ctx.value.beginPath();
    ctx.value.moveTo(startPos.value.x, startPos.value.y);
  } else if (currentTool.value === 'rectangle' || currentTool.value === 'circle' || currentTool.value === 'line') {
    // Save current canvas state for shapes
    tempCanvas.value = ctx.value.getImageData(0, 0, canvasRef.value.width, canvasRef.value.height);
  }
};

const draw = (e) => {
  const pos = getCanvasCoordinates(e);

  // Update eraser cursor position
  if (currentTool.value === 'eraser') {
    eraserPos.value = {
      x: pos.x - strokeWidth.value / 2,
      y: pos.y - strokeWidth.value / 2
    };
  }

  if (!isDrawing.value) return;

  if (currentTool.value === 'pen') {
    ctx.value.strokeStyle = currentColor.value;
    ctx.value.lineWidth = strokeWidth.value;
    ctx.value.lineTo(pos.x, pos.y);
    ctx.value.stroke();
  } else if (currentTool.value === 'eraser') {
    ctx.value.clearRect(
      pos.x - strokeWidth.value / 2,
      pos.y - strokeWidth.value / 2,
      strokeWidth.value,
      strokeWidth.value
    );
  } else if (currentTool.value === 'rectangle' || currentTool.value === 'circle' || currentTool.value === 'line') {
    // For shapes, restore temp canvas and draw preview
    if (tempCanvas.value) {
      ctx.value.putImageData(tempCanvas.value, 0, 0);
    }

    ctx.value.strokeStyle = currentColor.value;
    ctx.value.lineWidth = strokeWidth.value;

    if (currentTool.value === 'rectangle') {
      ctx.value.strokeRect(
        startPos.value.x,
        startPos.value.y,
        pos.x - startPos.value.x,
        pos.y - startPos.value.y
      );
    } else if (currentTool.value === 'circle') {
      const radius = Math.sqrt(
        Math.pow(pos.x - startPos.value.x, 2) + Math.pow(pos.y - startPos.value.y, 2)
      );
      ctx.value.beginPath();
      ctx.value.arc(startPos.value.x, startPos.value.y, radius, 0, 2 * Math.PI);
      ctx.value.stroke();
    } else if (currentTool.value === 'line') {
      ctx.value.beginPath();
      ctx.value.moveTo(startPos.value.x, startPos.value.y);
      ctx.value.lineTo(pos.x, pos.y);
      ctx.value.stroke();
    }
  }
};

const stopDrawing = () => {
  if (isDrawing.value) {
    isDrawing.value = false;
    saveToHistory();
    saveResponse();
  }
};

const handleMouseLeave = () => {
  stopDrawing();
  eraserPos.value = null;
  draggingLabel.value = null;
};

// Label management functions
const startDragLabel = (index, e) => {
  e.stopPropagation();
  e.preventDefault();
  draggingLabel.value = index;
  const label = textLabels.value[index];

  const clientX = e.touches ? e.touches[0].clientX : e.clientX;
  const clientY = e.touches ? e.touches[0].clientY : e.clientY;

  dragOffset.value = {
    x: clientX - label.x,
    y: clientY - label.y
  };

  const handleDrag = (moveEvent) => {
    if (draggingLabel.value === index) {
      const moveX = moveEvent.touches ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const moveY = moveEvent.touches ? moveEvent.touches[0].clientY : moveEvent.clientY;
      const rect = canvasRef.value.getBoundingClientRect();
      textLabels.value[index].x = moveX - rect.left - dragOffset.value.x + rect.left;
      textLabels.value[index].y = moveY - rect.top - dragOffset.value.y + rect.top;
    }
  };

  const handleStopDrag = () => {
    draggingLabel.value = null;
    document.removeEventListener('mousemove', handleDrag);
    document.removeEventListener('mouseup', handleStopDrag);
    document.removeEventListener('touchmove', handleDrag);
    document.removeEventListener('touchend', handleStopDrag);
    saveToHistory();
    saveResponse();
  };

  document.addEventListener('mousemove', handleDrag);
  document.addEventListener('mouseup', handleStopDrag);
  document.addEventListener('touchmove', handleDrag);
  document.addEventListener('touchend', handleStopDrag);
};

const deleteLabel = (index) => {
  textLabels.value.splice(index, 1);
  saveToHistory();
  saveResponse();
};

const confirmTextInput = () => {
  if (textInputValue.value.trim()) {
    textLabels.value.push({
      text: textInputValue.value,
      x: pendingTextPos.value.x,
      y: pendingTextPos.value.y,
      fontSize: fontSize.value,
      fontFamily: fontFamily.value,
      color: currentColor.value
    });
    saveToHistory();
    saveResponse();
  }
  showTextModal.value = false;
  textInputValue.value = '';
};

const cancelTextInput = () => {
  showTextModal.value = false;
  textInputValue.value = '';
};

const toggleTextBlock = () => {
  showTextBlock.value = !showTextBlock.value
  if (showTextBlock.value && selectedElement.value?.drawingConfig?.textBlockConfig) {
    textBlockPosition.value = selectedElement.value.drawingConfig.textBlockConfig.position || 'bottom'
  }
}

const setTextBlockPosition = (position) => {
  textBlockPosition.value = position;
};

// Touch support
const handleTouchStart = (e) => {
  e.preventDefault();
  const touch = e.touches[0];
  const mouseEvent = new MouseEvent('mousedown', {
    clientX: touch.clientX,
    clientY: touch.clientY
  });
  canvasRef.value.dispatchEvent(mouseEvent);
};

const handleTouchMove = (e) => {
  e.preventDefault();
  const touch = e.touches[0];
  const mouseEvent = new MouseEvent('mousemove', {
    clientX: touch.clientX,
    clientY: touch.clientY
  });
  canvasRef.value.dispatchEvent(mouseEvent);
};

const saveToHistory = () => {
  historyStep.value++;
  history.value = history.value.slice(0, historyStep.value);
  history.value.push(canvasRef.value.toDataURL());
};

const undo = () => {
  if (historyStep.value > 0) {
    historyStep.value--;
    restoreFromHistory();
    saveResponse();
  }
};

const redo = () => {
  if (historyStep.value < history.value.length - 1) {
    historyStep.value++;
    restoreFromHistory();
    saveResponse();
  }
};

const restoreFromHistory = () => {
  const img = new Image();
  img.onload = () => {
    ctx.value.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height);
    ctx.value.drawImage(img, 0, 0);
  };
  img.src = history.value[historyStep.value];
};

const clearCanvas = () => {
  const bgColor = selectedElement.value?.drawingConfig?.backgroundColor || '#ffffff'
  ctx.value.fillStyle = bgColor
  ctx.value.fillRect(0, 0, canvasRef.value.width, canvasRef.value.height)

  if (selectedElement.value?.drawingConfig?.backgroundImage) {
    const img = new Image()
    img.onload = () => {
      ctx.value.drawImage(img, 0, 0, canvasRef.value.width, canvasRef.value.height)
      saveToHistory()
      saveResponse()
    }
    img.src = selectedElement.value.drawingConfig.backgroundImage
  } else {
    saveToHistory()
    saveResponse()
  }
}

/**
 * Create a complete snapshot of the canvas including:
 * - Background image
 * - All drawings
 * - Text labels (rendered onto the canvas)
 * - Text block content (as overlay metadata)
 * This snapshot will be used for AI visual recognition in wasplcorrector
 */
const createCompleteSnapshot = () => {
  if (!canvasRef.value) return null

  // Create a temporary canvas for the complete snapshot
  const tempCanvas = document.createElement('canvas')
  tempCanvas.width = canvasRef.value.width
  tempCanvas.height = canvasRef.value.height
  const tempCtx = tempCanvas.getContext('2d')

  // 1. Draw the current canvas content (background + drawings)
  tempCtx.drawImage(canvasRef.value, 0, 0)

  // 2. Render text labels onto the canvas
  textLabels.value.forEach(label => {
    tempCtx.save()
    tempCtx.font = `${label.fontSize}px ${label.fontFamily}`
    tempCtx.fillStyle = label.color
    tempCtx.textBaseline = 'top'

    // Draw label background
    const metrics = tempCtx.measureText(label.text)
    const padding = 8
    tempCtx.fillStyle = 'rgba(255, 255, 255, 0.9)'
    tempCtx.fillRect(
      label.x - padding / 2,
      label.y - padding / 2,
      metrics.width + padding,
      label.fontSize + padding
    )

    // Draw label border
    tempCtx.strokeStyle = '#ddd'
    tempCtx.lineWidth = 1
    tempCtx.strokeRect(
      label.x - padding / 2,
      label.y - padding / 2,
      metrics.width + padding,
      label.fontSize + padding
    )

    // Draw text
    tempCtx.fillStyle = label.color
    tempCtx.fillText(label.text, label.x, label.y)
    tempCtx.restore()
  })

  // 3. If text block is visible, render it as an overlay on the canvas
  if (showTextBlock.value && textBlockContent.value.trim()) {
    tempCtx.save()
    const blockPadding = 15
    const blockFontSize = selectedElement.value?.drawingConfig?.textBlockConfig?.fontSize || 14
    const blockFontFamily = selectedElement.value?.drawingConfig?.textBlockConfig?.fontFamily || 'Arial'
    const blockBgColor = selectedElement.value?.drawingConfig?.textBlockConfig?.backgroundColor || '#ffffff'

    tempCtx.font = `${blockFontSize}px ${blockFontFamily}`

    // Split text into lines
    const lines = textBlockContent.value.split('\n')
    const lineHeight = blockFontSize * 1.5

    // Calculate block dimensions
    const maxLineWidth = Math.max(...lines.map(line => tempCtx.measureText(line).width))
    const blockWidth = Math.min(maxLineWidth + blockPadding * 2, tempCanvas.width - 40)
    const blockHeight = lines.length * lineHeight + blockPadding * 2

    // Position based on textBlockPosition
    let x, y
    switch (textBlockPosition.value) {
      case 'top':
        x = 20
        y = 20
        break
      case 'bottom':
        x = 20
        y = tempCanvas.height - blockHeight - 20
        break
      case 'left':
        x = 20
        y = (tempCanvas.height - blockHeight) / 2
        break
      case 'right':
        x = tempCanvas.width - blockWidth - 20
        y = (tempCanvas.height - blockHeight) / 2
        break
      default:
        x = 20
        y = tempCanvas.height - blockHeight - 20
    }

    // Draw text block background
    tempCtx.fillStyle = blockBgColor
    tempCtx.fillRect(x, y, blockWidth, blockHeight)

    // Draw text block border
    tempCtx.strokeStyle = '#ddd'
    tempCtx.lineWidth = 2
    tempCtx.strokeRect(x, y, blockWidth, blockHeight)

    // Draw text block header
    tempCtx.fillStyle = 'rgba(248, 249, 250, 0.95)'
    tempCtx.fillRect(x, y, blockWidth, 25)
    tempCtx.strokeRect(x, y, blockWidth, 25)

    tempCtx.fillStyle = '#333'
    tempCtx.font = `bold 12px ${blockFontFamily}`
    tempCtx.fillText('Text Block', x + blockPadding, y + 8)

    // Draw text content
    tempCtx.fillStyle = '#000'
    tempCtx.font = `${blockFontSize}px ${blockFontFamily}`
    lines.forEach((line, index) => {
      tempCtx.fillText(
        line,
        x + blockPadding,
        y + 25 + blockPadding + index * lineHeight
      )
    })

    tempCtx.restore()
  }

  return tempCanvas.toDataURL('image/png')
}

const saveResponse = () => {
  if (!canvasRef.value) return

  // Create complete snapshot including all elements
  const completeSnapshot = createCompleteSnapshot()

  const response = {
    userId: userId,
    questionId: selectedElement.value.el_ID,
    type: selectedElement.value.el_Type,
    questionLabel: selectedElement.value.el_Text,
    el_ID: selectedElement.value.el_ID,
    el_Type: selectedElement.value.el_Type,
    answered: true,
    // Flattened structure: drawing data directly at response level
    drawingData: completeSnapshot, // Complete snapshot with all elements
    textBlockContent: textBlockContent.value, // Optional text content
    textBlockPosition: textBlockPosition.value,
    textLabels: textLabels.value, // Text labels metadata
    hasDrawing: history.value.length > 0,
    score: 0,
    maxScore: selectedElement.value.maxScore || 1,
    timestamp: new Date().toISOString()
  }

  responsesStore.saveResponse(response)
  emit('interaction-started', selectedElement.value.el_ID)
}

// Methods for report and reset
const closeReport = () => {
  showReport.value = false
}

const showReportOnSubmit = () => {
  showReport.value = true
  currentDate.value = new Date().toLocaleString()
  saveResponse()
}

const resetAnswers = () => {
  clearCanvas()
  showReport.value = false
  textBlockContent.value = ''
  showTextBlock.value = false
  textLabels.value = []

  responsesStore.saveResponse(selectedElement.value.el_ID, null)
}

// Helper function to decode JWT token
function decodeToken(token) {
  if (!token) {
    console.error('Le token est null ou non défini')
    return null
  }
  try {
    const payload = token.split('.')[1]
    return JSON.parse(atob(payload))
  } catch (error) {
    console.error('Erreur lors du décodage du token :', error)
    return null
  }
}

onMounted(() => {
  if (quillEditor.value) {
    quillInstance = new Quill(quillEditor.value, {
      theme: 'snow',
      readOnly: true,
      modules: { toolbar: false }
    })

    if (selectedElement.value?.el_RichText) {
      quillInstance.root.innerHTML = selectedElement.value.el_RichText
    }
  }

  nextTick(() => {
    initCanvas()
  })
})

watch(() => props.question, (newElement) => {
  if (newElement && quillInstance) {
    quillInstance.root.innerHTML = newElement.el_RichText || ''
  }

  nextTick(() => {
    initCanvas()
  })
}, { deep: true })

onBeforeUnmount(() => {
  if (canvasRef.value && ctx.value) {
    saveResponse()
  }
})
</script>

<style scoped>
.preview-section {
  padding: 1rem;
}

.readonly-mode {
  pointer-events: none;
  opacity: 0.7;
}

.ReportContainer {
  background-color: #e9ecef;
  padding: 1rem;
  border-radius: 0.5rem;
  margin-top: 1rem;
}

.instructions {
  color: #666;
  margin-bottom: 1rem;
}

.drawing-area {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.canvas-wrapper {
  position: relative;
  display: inline-block;
  max-width: 100%;
}

canvas {
  cursor: crosshair;
  display: block;
  max-width: 100%;
  height: auto;
  touch-action: none;
}

.tools-panel {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  width: 100%;
  justify-content: center;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.btn-group button {
  min-width: 70px;
}

.action-buttons {
  display: flex;
  gap: 0.25rem;
}

.color-picker,
.stroke-width {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.eraser-cursor {
  position: absolute;
  border: 2px dashed #ff0000;
  background-color: rgba(255, 0, 0, 0.1);
  pointer-events: none;
  transform: translate(-50%, -50%);
  border-radius: 2px;
  z-index: 10;
}

.text-label {
  position: absolute;
  white-space: nowrap;
  user-select: none;
  padding: 4px 8px;
  background-color: rgba(255, 255, 255, 0.9);
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 20;
  transition: box-shadow 0.2s;
}

.text-label:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  border-color: #007bff;
}

.delete-label-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background-color: #dc3545;
  color: white;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  display: none;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.text-label:hover .delete-label-btn {
  display: flex;
}

.delete-label-btn:hover {
  background-color: #c82333;
}

.text-input-modal {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 30;
  width: 90%;
  max-width: 400px;
}

.text-input-modal .modal-content {
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.text-input-modal h5 {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.text-input-modal .form-control {
  width: 100%;
  margin-bottom: 12px;
}

.text-input-modal .modal-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.text-block-overlay {
  position: absolute;
  z-index: 25;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: transparent;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}

/* Position variants */
.text-block-overlay.position-top {
  top: 0;
  left: 0;
  right: 0;
}

.text-block-overlay.position-bottom {
  bottom: 0;
  left: 0;
  right: 0;
}

.text-block-overlay.position-left {
  top: 0;
  left: 0;
  bottom: 0;
  width: 300px;
}

.text-block-overlay.position-right {
  top: 0;
  right: 0;
  bottom: 0;
  width: 300px;
}

.text-block-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 10px;
  background: rgba(248, 249, 250, 0.95);
  border-bottom: 1px solid #ddd;
  border-radius: 4px 4px 0 0;
  flex-shrink: 0;
}

.text-block-header span {
  font-weight: 600;
  font-size: 12px;
  color: #333;
}

.position-buttons {
  display: flex;
  gap: 3px;
}

.position-buttons button {
  width: 26px;
  height: 26px;
  border: 1px solid #ccc;
  background: white;
  border-radius: 3px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  padding: 0;
}

.position-buttons button:hover {
  background: #e9ecef;
  border-color: #999;
}

.position-buttons button.active {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.text-block-textarea {
  width: 100%;
  border: none;
  border-radius: 0 0 4px 4px;
  resize: none;
  outline: none;
  font-family: inherit;
  flex: 1;
  min-height: 60px;
}

.text-block-textarea:focus {
  outline: 2px solid #007bff;
  outline-offset: -2px;
}

/* Left/Right position specific styles */
.text-block-overlay.position-left .text-block-textarea,
.text-block-overlay.position-right .text-block-textarea {
  height: 100%;
}

@media (max-width: 768px) {
  .tools-panel {
    flex-direction: column;
    align-items: stretch;
  }

  .btn-group,
  .action-buttons {
    width: 100%;
    justify-content: center;
  }

  .color-picker,
  .stroke-width {
    justify-content: center;
  }
}
</style>
